import { useCallback, useEffect, useRef } from 'react';

// User interactions that count as "activity" and reset the inactivity timer.
const DEFAULT_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'wheel'
];

// Ignore bursts of activity within this window so we are not clearing and
// re-arming the timers on every pixel of mouse movement.
const ACTIVITY_THROTTLE_MS = 1000;

interface UseIdleTimerOptions {
  // Milliseconds of inactivity after which `onIdle` fires.
  timeout: number;
  // Milliseconds before `onIdle` at which `onPrompt` fires (the warning
  // window). Set to 0 to disable the warning phase.
  promptBeforeIdle?: number;
  // Called once the full `timeout` of inactivity elapses.
  onIdle: () => void;
  // Called `promptBeforeIdle` ms before the idle deadline.
  onPrompt?: () => void;
  // While false the timers are disabled and all listeners removed.
  enabled?: boolean;
  events?: string[];
}

interface UseIdleTimerResult {
  // Restart the countdown from zero (e.g. when the user chooses to stay).
  reset: () => void;
}

// Tracks user activity on the window and invokes callbacks when the user has
// been idle. Once the warning phase begins, ambient activity is ignored: the
// user must explicitly reset (via `reset()`) to keep the session, which
// guarantees the hard logout still fires within `timeout` of the last real
// interaction.
const useIdleTimer = ({
  timeout,
  promptBeforeIdle = 0,
  onIdle,
  onPrompt,
  enabled = true,
  events = DEFAULT_EVENTS
}: UseIdleTimerOptions): UseIdleTimerResult => {
  const idleTimer = useRef<ReturnType<typeof setTimeout>>();
  const promptTimer = useRef<ReturnType<typeof setTimeout>>();
  const prompted = useRef<boolean>(false);
  const lastActivity = useRef<number>(0);

  // Keep the latest callbacks in refs so activity listeners are bound once and
  // do not need re-binding when a parent re-renders with new callbacks.
  const onIdleRef = useRef(onIdle);
  const onPromptRef = useRef(onPrompt);
  onIdleRef.current = onIdle;
  onPromptRef.current = onPrompt;

  const clearTimers = useCallback(() => {
    if (idleTimer.current) {
      clearTimeout(idleTimer.current);
    }
    if (promptTimer.current) {
      clearTimeout(promptTimer.current);
    }
  }, []);

  const start = useCallback(() => {
    clearTimers();
    prompted.current = false;

    if (promptBeforeIdle > 0 && onPromptRef.current) {
      promptTimer.current = setTimeout(() => {
        prompted.current = true;
        if (onPromptRef.current) {
          onPromptRef.current();
        }
      }, Math.max(timeout - promptBeforeIdle, 0));
    }

    idleTimer.current = setTimeout(() => {
      onIdleRef.current();
    }, timeout);
  }, [clearTimers, promptBeforeIdle, timeout]);

  const reset = useCallback(() => {
    lastActivity.current = Date.now();
    start();
  }, [start]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      return undefined;
    }

    const handleActivity = () => {
      // During the warning window, only an explicit reset() keeps the session.
      if (prompted.current) {
        return;
      }
      const now = Date.now();
      if (now - lastActivity.current < ACTIVITY_THROTTLE_MS) {
        return;
      }
      lastActivity.current = now;
      start();
    };

    lastActivity.current = Date.now();
    start();
    events.forEach(evt =>
      window.addEventListener(evt, handleActivity, { passive: true })
    );

    return () => {
      clearTimers();
      events.forEach(evt => window.removeEventListener(evt, handleActivity));
    };
    // `events` is a stable module-level default; callers passing an inline
    // array should memoize it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, start, clearTimers]);

  return { reset };
};

export default useIdleTimer;
